import React, { useMemo, useState } from 'react';
import { useHideOverflowing } from 'components/common/useHideOverflowing';
import { useTranslation } from 'react-i18next';
import {
  AttentionIcon,
  CopyToClipboard,
  RefreshSpinner,
} from 'components/common/ProjectIcons';
import { toast } from 'react-toastify';
import { Destination, Source } from 'components/common/ObjectTypes';
import NoResults from 'components/common/NoResults';
import Pagination from 'components/common/Pagination';
import { page_size } from 'api/apiUtils';
import { useTimezone } from 'context/TimezoneContext';
import { formatSourceDatetime } from 'components/sources/SourceHelperFunctions';
import {
  tableClasses,
  paginationClasses,
  filterClasses,
  buttonClasses,
  onTealText,
} from 'components/common/tailwindClassnames';
import GlobalSearch from 'components/common/GlobalSearch';
import SelectInput from 'components/common/SelectInput';
import SortByDropdown, { SortOption } from 'components/common/SortByDropdown';
import SourceDrawer from './sourceFilters/SourceDrawer';
import AddDeviceModal from './sourceFilters/AddDeviceModal';
import ManualBadge from './sourceFilters/ManualBadge';

type FilterOption = { label: string; value: string };

export type RouteSourceProps = {
  sources: Source[];
  totalSources: number;
  globalSearchStr: string;
  take: number;
  offset: number;
  onPageClicked: Function;
  loadingSources: boolean;
  pageLoaded: boolean;
  destinationUrls: string[];
  loadingDestinationUrls: boolean;
  destinationUrlFilterValue: FilterOption | null;
  onChangeGlobalSourceSearch: (value: string) => void;
  onRefresh: () => void;
  onChangeDestinationUrlFilter: Function;
  // Opens one of the Route's destinations. The Route owns the navigation —
  // the URL it builds keeps the id the page is addressed by.
  onViewDestination: (destinationId: string) => void;
  ordering: string;
  onChangeSortBy: (selected: SortOption) => void;
  // Adding a device by hand (GUNDI-5178). The parent owns the request and infers
  // the provider from the route.
  onCreateSource?: (payload: {
    external_id: string;
    name?: string;
  }) => Promise<void>;
};

const RouteDetailsSource = ({
  sources,
  totalSources,
  globalSearchStr,
  take,
  offset,
  onPageClicked,
  pageLoaded,
  loadingSources,
  destinationUrls,
  loadingDestinationUrls,
  destinationUrlFilterValue,
  onChangeGlobalSourceSearch,
  onRefresh,
  onChangeDestinationUrlFilter,
  onViewDestination,
  ordering,
  onChangeSortBy,
  onCreateSource,
}: RouteSourceProps) => {
  // Phones: a control that does not fully fit in its row is hidden, not cut in half.
  const searchRowRef = useHideOverflowing<HTMLDivElement>();
  const filterRowRef = useHideOverflowing<HTMLDivElement>();
  const { t } = useTranslation();
  // The view slide-out (null = closed); creating goes through the shared modal.
  const [drawerSource, setDrawerSource] = useState<Source | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const { timezone } = useTimezone();

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('External ID (A-Z)'), value: 'external_id' },
      { label: t('External ID (Z-A)'), value: '-external_id' },
      { label: t('Newest First'), value: '-created_at' },
      { label: t('Oldest First'), value: 'created_at' },
    ],
    [t],
  );

  const hasActiveFilters = !!(globalSearchStr || destinationUrlFilterValue);

  const clearAllFilters = () => {
    onChangeGlobalSourceSearch('');
    onChangeDestinationUrlFilter(null);
  };

  return (
    <div className="mt-8">
      <div className="my-3 flex flex-col">
        {/* Search row */}
        <div
          ref={searchRowRef}
          className="mb-3 flex flex-row flex-nowrap items-end gap-3 overflow-x-clip"
        >
          <GlobalSearch
            onChangeGlobalSearch={onChangeGlobalSourceSearch}
            placeholder={t('Name')}
            savedString={globalSearchStr}
          />
          <SortByDropdown
            options={sortOptions}
            ordering={ordering}
            onChangeSortBy={onChangeSortBy}
          />
          <button
            type="button"
            onClick={() => onRefresh()}
            aria-label={t('Refresh list')}
            disabled={loadingSources}
            className={`mb-2 ml-auto flex h-[38px] w-8 cursor-pointer items-center justify-center self-end ${loadingSources ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
          >
            <RefreshSpinner />
          </button>
          {hasActiveFilters && (
            <button
              className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
              onClick={clearAllFilters}
            >
              {t('Clear All Filters')}
            </button>
          )}
        </div>
        {/* Filter dropdowns row */}
        <div
          ref={filterRowRef}
          className="mb-3 flex flex-row flex-nowrap gap-3 overflow-x-clip"
        >
          <div className="flex flex-col gap-1">
            <span className={filterClasses.label}>{t('Destination')}</span>
            <SelectInput
              name="destination_url__in"
              options={destinationUrls.map((url) => {
                let hostname = url;
                try {
                  hostname = new URL(url).hostname;
                } catch {}
                return { label: hostname, value: url };
              })}
              value={destinationUrlFilterValue}
              onChange={onChangeDestinationUrlFilter}
              isMulti={false}
              isClearable={true}
              isSearchable={true}
              isDisabled={false}
              isLoading={loadingDestinationUrls}
              className="w-48"
              variant="routeFilter"
            />
          </div>
        </div>
        <div className="flex w-full flex-col">
          <div className="flex w-full flex-row items-end justify-between gap-3">
            <label className={`${onTealText} transition-colors duration-300`}>
              {t('Total Sources')}: {totalSources}
            </label>
            {onCreateSource && (
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className={`${buttonClasses.primaryAction} whitespace-nowrap`}
                data-testid="RouteDetailsSources-add"
              >
                {t('Add Source')}
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className={`mt-3 ${tableClasses.container} cursor-pointer`}>
              <thead className={tableClasses.header}>
                <tr>
                  <th className={tableClasses.headerCell}>
                    {t('External ID')}
                  </th>
                  <th className={tableClasses.headerCell}>{t('Name')}</th>
                  <th className={tableClasses.headerCell}>
                    {t('Destination')}
                  </th>
                  <th className={tableClasses.headerCell}>
                    {t('Date Created')}
                  </th>
                </tr>
              </thead>
              {loadingSources ? (
                <tbody
                  className="w-full text-sm"
                  role="status"
                  aria-busy="true"
                >
                  <tr className="sr-only">
                    <td>Loading...</td>
                  </tr>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className={tableClasses.row}>
                      <td className={tableClasses.cell}>
                        <div
                          className={`h-4 ${i % 3 === 0 ? 'w-44' : i % 3 === 1 ? 'w-32' : 'w-52'} animate-pulse rounded bg-teal-t30`}
                        />
                      </td>
                      <td className={tableClasses.cell}>
                        <div className="h-4 w-24 animate-pulse rounded bg-teal-t30" />
                      </td>
                      <td className={tableClasses.cell}>
                        <div
                          className={`h-4 ${i % 3 === 0 ? 'w-48' : i % 3 === 1 ? 'w-36' : 'w-40'} animate-pulse rounded bg-teal-t30`}
                        />
                      </td>
                      <td className={tableClasses.cell}>
                        <div className="h-4 w-32 animate-pulse rounded bg-teal-t30" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : sources.length < 1 ? (
                <tbody>
                  <tr>
                    <td colSpan={4} className="py-16">
                      <NoResults
                        headerMsg={t('No Results Found')}
                        labelMsg={t(
                          'You may need to configure your first source with this Route.',
                        )}
                      />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="text-sm">
                  {sources.map((source: Source) => {
                    return (
                      <tr key={source.id} className={tableClasses.row}>
                        <td className={tableClasses.cell}>
                          <span className="inline-flex flex-wrap items-center gap-2">
                            {source.status === 'healthy' ? (
                              ''
                            ) : (
                              <AttentionIcon />
                            )}
                            <button
                              type="button"
                              onClick={() => setDrawerSource(source)}
                              className="cursor-pointer text-left text-brand-accent-green hover:underline"
                              data-testid={`RouteDetailsSources-view-${source.external_id}`}
                            >
                              {source.external_id}
                            </button>
                            <ManualBadge
                              source={source}
                              testIdPrefix="RouteDetailsSources"
                              colorClassName="text-greyscale-6"
                            />
                          </span>
                        </td>
                        <td className={tableClasses.cell}>
                          {source.name || ''}
                        </td>
                        <td className={tableClasses.cell}>
                          {source.destinations.map((dest: Destination) => {
                            let dest_url: string;
                            try {
                              dest_url = new URL(dest.base_url).hostname;
                            } catch (err) {
                              dest_url = '';
                            }
                            return (
                              <div
                                key={dest.id}
                                className="flex items-center gap-2 text-brand-accent-green"
                              >
                                {/* The hostname has always been drawn in link colour; it
                                                                now goes where every other "open this destination from
                                                                a Route" click goes — the account on the flow map. */}
                                <button
                                  type="button"
                                  data-testid={`RouteDetailsSources-destination-${dest.id}`}
                                  className="cursor-pointer text-left hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewDestination(dest.id);
                                  }}
                                >
                                  {dest_url}
                                </button>
                                {dest.base_url && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(
                                        dest.base_url,
                                      );
                                      toast.info(
                                        `${t('URL')} ${t('copied to clipboard')}`,
                                        {
                                          position: toast.POSITION.TOP_RIGHT,
                                          autoClose: 2000,
                                        },
                                      );
                                    }}
                                    className="flex items-center justify-center opacity-50 transition-opacity hover:opacity-100"
                                    aria-label="Copy URL to clipboard"
                                  >
                                    <CopyToClipboard />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </td>
                        <td className={tableClasses.cell}>
                          {formatSourceDatetime(source.created_at, timezone)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
          {totalSources > page_size && (
            <Pagination
              showText={true}
              handlePageClick={onPageClicked}
              pageCount={Math.ceil(totalSources / take)}
              resultStart={offset + 1}
              resultEnd={
                offset + take < totalSources ? offset + take : totalSources
              }
              totalCount={totalSources}
              take={take}
              loading={!pageLoaded}
              mainclassname={paginationClasses.container}
            />
          )}
        </div>
      </div>
      <SourceDrawer
        isOpen={drawerSource !== null}
        onClose={() => setDrawerSource(null)}
        source={drawerSource}
      />
      {onCreateSource && (
        <AddDeviceModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreate={onCreateSource}
          testIdPrefix="RouteDetailsSources"
        />
      )}
    </div>
  );
};

export default RouteDetailsSource;
